import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { AppIconComponent } from '../brand/app-icon.component';
import { AppStatusComponent, AppStatusKind } from '../status/app-status.component';

/**
 * Scheda di un'App nel catalogo del cantiere (spec 004 FR-001, FR-003; tavola del catalogo): tessera,
 * nome, descrizione breve, figure professionali, stato e azioni proiettate. Il nome diventa un
 * collegamento quando l'App si può aprire; le azioni restano pulsanti a parte.
 */
@Component({
  selector: 'bs-app-tile',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AppIconComponent, AppStatusComponent, RouterLink],
  host: { class: 'block' },
  template: `
    <article
      class="flex h-full flex-col gap-4 rounded-3xl border border-stone-200 bg-white p-5 dark:border-stone-700 dark:bg-stone-900"
      [attr.data-testid]="testId()"
    >
      <div class="flex items-start gap-3.5">
        <bs-app-icon [code]="glyph()" [muted]="muted()" />
        <div class="flex min-w-0 flex-1 flex-col gap-1">
          <h3 class="text-base font-semibold text-ink dark:text-stone-50">
            @if (link(); as target) {
              <a
                [routerLink]="target"
                class="rounded-sm underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-bsite-500"
                >{{ name() }}</a
              >
            } @else {
              {{ name() }}
            }
          </h3>
          <p class="text-sm text-stone-600 dark:text-stone-300">{{ summary() }}</p>
        </div>
        <bs-app-status [status]="status()" [label]="statusLabel()" />
      </div>
      @if (audience().length > 0) {
        <ul class="flex flex-wrap gap-1.5" aria-label="Utile a">
          @for (item of audience(); track item) {
            <li
              class="rounded-full bg-stone-100 px-2.5 py-0.5 text-xs font-medium text-stone-700 dark:bg-stone-800 dark:text-stone-200"
            >
              {{ item }}
            </li>
          }
        </ul>
      }
      @if (note(); as text) {
        <p class="text-sm text-stone-600 dark:text-stone-300">{{ text }}</p>
      }
      <div class="mt-auto flex flex-wrap items-center gap-2 empty:hidden">
        <ng-content />
      </div>
    </article>
  `,
})
export class AppTileComponent {
  /** Lettera della tessera dell'App. */
  readonly glyph = input.required<string>();
  readonly name = input.required<string>();
  readonly summary = input('');
  /** Figure professionali a cui l'App è utile, già in italiano. */
  readonly audience = input<readonly string[]>([]);
  readonly status = input.required<AppStatusKind>();
  readonly statusLabel = input<string | null>(null);
  /** Spiegazione dello stato, per esempio perché non è attivabile. */
  readonly note = input<string | null>(null);
  readonly link = input<string | readonly unknown[] | null>(null);
  readonly testId = input<string | null>(null);

  protected readonly muted = computed(() =>
    ['coming_soon', 'not_in_plan', 'inactive', 'retired'].includes(this.status()),
  );
}
